import React, { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CosignNudges from "./CosignNudges";
import ArtistAvatar from "./components/ArtistAvatar";
import FallbackTrackPlayer from "./artistsTable/FallbackTrackPlayer";
import { pickSoundcloudLink } from "./artistsTable/artistsTableUtils";
import useViewer from "./hooks/useViewer";
import useBoardData from "./hooks/useBoardData";
import useArtistRankings from "./useArtistRankings";
import useHomeCity from "./home/useHomeCity";
import { CITY_COORDS } from "./usPlaces";
import {
  placeArtists, sceneFor, biggestCity, hubCity, cityChoices, cityLabel, cosignRows,
  trendingRows, globePins, formatPct, pctTone,
} from "./home/sceneModel";
import "./home/home.css";
import Loader from "./components/Loader";
import { artistPath } from "./artistPath";

// The globe is decoration: its dot data loads in its own chunk after the
// page, and if that chunk can't load the hero simply goes without it.
const SceneGlobe = lazy(() => import("./home/SceneGlobe").catch(() => ({ default: () => null })));

// The homepage (/ and /nexxup), top to bottom:
// - "Who's moving in <your city>": a dot globe that flies to the viewer's
//   city (a signed-in fan's saved city, else a time zone guess; the chip
//   changes it or asks for their exact location, see home/useHomeCity) and
//   that city's top 5 by Motion. A thin
//   city widens to its state, else to the nearest big scene
//   (home/sceneModel.js sceneFor).
// - Signed in: "Your co-signs this week".
// - The scouting pitch: co-sign early, earn Scout Score.
// - "Trending across the US this week": the board's biggest weekly gainers.
// - Three ways in (leaderboard, heat map, Discover) and, signed out, the
//   claim-your-profile band for artists.
// Everything reads the same /api/artists board as the leaderboard
// (useBoardData), so going from one page to the other doesn't refetch it.
function Home() {
  const viewer = useViewer();
  const board = useBoardData();
  const ranking = useArtistRankings(viewer.user?.uid);
  const [rankingQueue, setRankingQueue] = useState(null);
  const home = useHomeCity(viewer.userProfile?.city);

  // Page background on the brand surface (tokens). Body already defaults to
  // these, but the homepage sets them explicitly and restores on unmount.
  useEffect(() => {
    document.body.style.backgroundColor = "var(--nx-bg)";
    document.body.style.color = "var(--nx-text)";
    return () => {
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    };
  }, []);

  const { artistNames, artistsCollection, nguageByArtist, allDateTimes, weeklyGainers } = board;
  const model = useMemo(
    () => ({ artistNames, artistsCollection, nguageByArtist, allDateTimes, weeklyGainers }),
    [artistNames, artistsCollection, nguageByArtist, allDateTimes, weeklyGainers]
  );
  const hasBoard = artistNames.length > 0;
  const placed = useMemo(() => placeArtists(artistNames, artistsCollection), [artistNames, artistsCollection]);
  // An exact location resolves to its nearest tracked city, which can be a
  // suburb; show the metro's main city instead.
  const cityKey = home.cityKey
    ? (home.source === "exact" ? hubCity(home.cityKey, placed) : home.cityKey)
    : (placed.length > 0 ? biggestCity(placed) : null);
  const scene = useMemo(
    () => (hasBoard && cityKey ? sceneFor(cityKey, placed, model) : null),
    [hasBoard, cityKey, placed, model]
  );
  const pins = useMemo(() => globePins(placed, model), [placed, model]);
  const choices = useMemo(() => cityChoices(placed, cityKey), [placed, cityKey]);
  const cosigns = useMemo(
    () => (viewer.user ? cosignRows(viewer.bookmarkedArtists, model) : { rows: [], up: 0 }),
    [viewer.user, viewer.bookmarkedArtists, model]
  );
  const trending = useMemo(() => trendingRows(model, 5), [model]);

  const signedIn = viewer.authReady && !!viewer.user;
  const signedOut = viewer.authReady && !viewer.user;
  const accountPath = viewer.userProfile?.accountType === "artist" ? "/artist-account" : "/fan-account";

  return (
    <div className="nx-home">
      <CosignNudges
        viewer={viewer}
        ranking={ranking}
        weeklyGainers={weeklyGainers}
        rankingQueue={rankingQueue}
        setRankingQueue={setRankingQueue}
        showMovers={false}
      />

      <SceneHero
        scene={scene}
        cityKey={cityKey}
        loading={!hasBoard}
        failed={!!board.error && !hasBoard}
        errorMessage={board.error}
        onRetry={board.reload}
        pins={pins}
        choices={choices}
        home={home}
      />

      {signedIn && cosigns.rows.length > 0 && (
        <section className="nx-home-section nx-home-wrap" aria-labelledby="nx-home-cosigns">
          <div className="nx-home-section-head">
            <div>
              <h2 id="nx-home-cosigns" className="nx-home-h2">Your co-signs this week</h2>
              <p className="nx-home-sub">{cosigns.up} of {cosigns.rows.length} moved up this week</p>
            </div>
            <Link to={accountPath} className="nx-home-more">All →</Link>
          </div>
          <div className="nx-home-cosigns">
            {cosigns.rows.slice(0, 4).map((c) => (
              <Link key={c.artistKey} to={artistPath(c.artistName)} className="nx-home-cosign nx-card">
                <span className="nx-home-cosign-who">
                  <ArtistAvatar name={c.artistName} photoURL={c.photoURL} size={44} />
                  <span className="nx-home-ellipsis-col">
                    <span className="nx-home-name">{c.artistName}</span>
                    {c.place && <span className="nx-home-place-text">{c.place}</span>}
                  </span>
                </span>
                {c.weekPct !== null && (
                  <span className={`nx-home-cosign-week nx-tone-${pctTone(c.weekPct)}`}>
                    {formatPct(c.weekPct)} this week
                  </span>
                )}
                {c.sincePct !== null && (
                  <span className="nx-home-cosign-since">
                    {c.sincePct >= 0 ? "Up" : "Down"} {Math.abs(Math.round(c.sincePct))}% since you co-signed
                  </span>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      <ScoutBand signedIn={signedIn} />

      {trending.length > 0 && (
        <section className="nx-home-section" aria-labelledby="nx-home-trending">
          <div className="nx-home-section-head nx-home-wrap">
            <h2 id="nx-home-trending" className="nx-home-h2">
              Trending across the US<span className="nx-home-wide-only"> this week</span>
            </h2>
            <Link to="/leaderboard" className="nx-home-more">
              <span className="nx-home-wide-only">Full leaderboard</span>
              <span className="nx-home-narrow-only">All</span> →
            </Link>
          </div>
          <div className="nx-home-trending nx-home-wrap">
            {trending.map((t) => (
              <Link key={t.artistKey} to={artistPath(t.artistName)} className="nx-home-tile">
                <ArtistTile name={t.artistName} photoURL={t.photoURL} />
                <span className="nx-home-name nx-home-tile-name">{t.artistName}</span>
                {t.place && <span className="nx-home-place-text">{t.place}</span>}
                <span className="nx-home-tile-stats">
                  {t.motion !== null && <span className="nx-home-tile-motion">MOTION {t.motion.toFixed(1)}</span>}{" "}
                  <span className={`nx-tone-${pctTone(t.weekPct)}`}>{formatPct(t.weekPct)}</span>
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="nx-home-section nx-home-wrap" aria-labelledby="nx-home-ways">
        <h2 id="nx-home-ways" className="nx-home-h2">Three ways to dig</h2>
        <div className="nx-home-ways">
          {WAYS.map((w) => (
            <Link key={w.to} to={w.to} className="nx-home-way nx-card">
              <span className="nx-home-way-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d={w.icon} /></svg>
              </span>
              <span className="nx-home-way-title">{w.title} →</span>
              <span className="nx-home-way-text">{w.text}</span>
            </Link>
          ))}
        </div>
      </section>

      {signedOut && (
        <section className="nx-home-section nx-home-wrap" aria-labelledby="nx-home-artists">
          <div className="nx-home-artists nx-card">
            <div className="nx-home-artists-copy">
              <p className="nx-kicker">For artists</p>
              <h2 id="nx-home-artists" className="nx-home-artists-title">Already on the board? Claim your profile.</h2>
              <p className="nx-home-sub">See your Motion, your numbers across every platform, your shows and your link page in one place.</p>
            </div>
            <Link to="/signup?as=artist" className="nx-home-btn nx-home-btn-ghost">Claim your profile</Link>
          </div>
        </section>
      )}

      <footer className="nx-home-footer nx-home-wrap">
        <span>© Nexxup</span>
        <Link to="/help">Help</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/terms">Terms</Link>
      </footer>
    </div>
  );
}

const WAYS = [
  { to: "/leaderboard", title: "Leaderboard", text: "Every artist ranked by Motion, with the week's biggest movers.", icon: "M5 20V11M12 20V5M19 20v-8" },
  { to: "/heatmap", title: "Heat map", text: "Where the scenes are. Zoom into any city for its own board.", icon: "M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21zM12 7v5" },
  { to: "/discover", title: "Discover", text: "Browse every genre fans have mapped, from Rage to UK Garage.", icon: "M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM15.5 8.5l-2 5-5 2 2-5z" },
];

// Warms the heat map's chunk (MapLibre and all) when a pointer heads for the
// globe, so the tap lands on a page that's already downloading. Nothing
// loads until then: the homepage stays light.
function prefetchHeatmap() {
  import("./heatmap").catch(() => {});
}

const PIN_ICON = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21z" />
    <circle cx="12" cy="9.5" r="2.5" />
  </svg>
);

function SceneHero({ scene, cityKey, loading, failed, errorMessage, onRetry, pins, choices, home }) {
  const [picking, setPicking] = useState(false);
  const placeRef = useRef(null);

  // The picker closes on a pick, Escape, or a click anywhere else.
  useEffect(() => {
    if (!picking) return undefined;
    const onDown = (e) => { if (placeRef.current && !placeRef.current.contains(e.target)) setPicking(false); };
    const onKey = (e) => { if (e.key === "Escape") setPicking(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [picking]);

  // A successful "exact location" closes the picker too.
  const { source } = home;
  useEffect(() => { if (source === "exact") setPicking(false); }, [source, cityKey]);

  const ranked = useMemo(() => (scene ? scene.rows.map((r, i) => ({ key: r.artistKey, rank: i + 1 })) : []), [scene]);
  const you = scene && (scene.scope === "state" || scene.scope === "nearest") ? CITY_COORDS[cityKey] : null;
  const focus = useMemo(() => {
    if (!scene) return null;
    if (scene.focus.length > 0) return scene.focus;
    return CITY_COORDS[cityKey] ? [CITY_COORDS[cityKey]] : null;
  }, [scene, cityKey]);
  const title = scene ? scene.title : "your city";
  const label = scene ? `Globe centred on ${scene.title}, showing the artists moving there` : "Globe, finding your city";
  // The globe opens the heat map at the same place (heatmap.jsx reads the
  // same ?city= / ?state= the leaderboard does).
  const heatmapHref = scene?.query ? `/heatmap?${scene.query}` : "/heatmap";

  return (
    <section className="nx-home-hero" aria-label="Your scene">
      <div className="nx-home-hero-stage">
        <Suspense fallback={null}>
          <SceneGlobe pins={pins} ranked={ranked} you={you} focus={focus} label={label} />
        </Suspense>
        <div className="nx-home-hero-fade" aria-hidden="true" />
        <Link
          to={heatmapHref}
          className="nx-home-globe-link"
          aria-label={scene && scene.scope !== "country" ? `Open the heat map at ${scene.title}` : "Open the heat map"}
          onPointerEnter={prefetchHeatmap}
          onTouchStart={prefetchHeatmap}
        >
          <span className="nx-home-globe-cta" aria-hidden="true">Explore the globe →</span>
        </Link>
      </div>

      <div className="nx-home-wrap nx-home-hero-inner">
        <div className="nx-home-hero-col">
          <div className="nx-home-place" ref={placeRef}>
            <button type="button" className="nx-home-place-chip" aria-expanded={picking} onClick={() => setPicking((v) => !v)}>
              <span className="nx-home-place-pin">{PIN_ICON}</span>
              {cityKey ? cityLabel(cityKey) : "Finding your scene…"}
              <span className="nx-home-place-change">· Change</span>
            </button>
            {picking && (
              <div className="nx-home-picker" role="group" aria-label="Pick a city">
                <p className="nx-kicker">Show another city</p>
                <div className="nx-home-picker-chips">
                  {choices.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className="nx-chip nx-home-picker-chip"
                      aria-pressed={c.key === cityKey}
                      onClick={() => { home.pick(c.key); setPicking(false); }}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <button type="button" className="nx-home-picker-exact" onClick={home.locate} disabled={home.locating}>
                  {PIN_ICON}{home.locating ? "Finding you…" : "Use my exact location"}
                </button>
                {home.locateError && <p className="nx-home-picker-error" role="status">{home.locateError}</p>}
              </div>
            )}
          </div>

          <h1 className="nx-headline nx-home-h1">
            Who's moving in<br className="nx-home-wide-only" />{" "}
            <span className={scene ? "nx-home-h1-city" : "nx-home-h1-city is-pending"}>{title}</span>
          </h1>
          <p className="nx-home-lede">
            Nexxup tracks underground artists' reach every day. These five have the most Motion near you.
          </p>
          {scene?.note && <p className="nx-home-note">{scene.note}</p>}

          <ol className="nx-home-top" aria-label={`Top artists in ${title}`}>
            {scene && scene.rows.map((r, i) => (
              <li key={r.artistKey} className="nx-home-top-row">
                <span className="nx-home-top-rank">{i + 1}</span>
                <Link to={artistPath(r.artistName)} className="nx-home-top-who">
                  <ArtistAvatar name={r.artistName} photoURL={r.photoURL} size={40} />
                  <span className="nx-home-ellipsis-col">
                    <span className="nx-home-name">{r.artistName}</span>
                    <span className="nx-home-top-sub">
                      MOTION {r.motion.toFixed(1)}{scene.scope !== "city" && r.town ? ` · ${r.town.toUpperCase()}` : ""}
                    </span>
                  </span>
                </Link>
                {r.weekPct !== null && (
                  <span className={`nx-home-top-change nx-tone-${pctTone(r.weekPct)}`}>{formatPct(r.weekPct)}</span>
                )}
                <FallbackTrackPlayer
                  variant="outline"
                  artistId={r.artistKey}
                  artistName={r.artistName}
                  spotifyId={r.profile?.spotify_id || null}
                  soundcloudId={r.profile?.soundcloud_id || null}
                  youtubeId={r.profile?.youtube_id || null}
                  soundcloudUrl={pickSoundcloudLink(null, r.profile?.soundcloud_url, null)}
                />
              </li>
            ))}
          </ol>
          {!scene && (
            <p className="nx-home-top-empty">
              {failed ? (errorMessage || "Couldn't load the artists right now.") : loading ? <Loader size="inline" label="Finding the artists near you…" /> : ""}
            </p>
          )}
          {failed && (
            <button type="button" className="nx-home-btn nx-home-btn-ghost" onClick={onRetry}>Retry</button>
          )}

          <div className="nx-home-hero-ctas">
            <Link to={scene?.query ? `/leaderboard?${scene.query}` : "/leaderboard"} className="nx-home-btn nx-home-btn-primary">
              {scene && scene.scope !== "country" ? `See the full ${scene.title} board` : "See the full board"}
            </Link>
            <Link to={heatmapHref} className="nx-home-link">Open the heat map →</Link>
          </div>
        </div>
      </div>
    </section>
  );
}

const STEPS = [
  ["Co-sign early", "Back an artist while they're still underground."],
  ["Watch them move", "Their Motion is tracked every day, on every platform."],
  ["Earn Scout Score", "When they grow, your Scout Score grows with them."],
];

function ScoutBand({ signedIn }) {
  // One grid: on phones the steps sit between the pitch and the button, on
  // desktop they take the right-hand column (home.css).
  return (
    <section className="nx-home-section nx-home-wrap" aria-labelledby="nx-home-scout">
      <div className="nx-home-scout nx-card">
        <div className="nx-home-scout-copy">
          <p className="nx-kicker">Scouting</p>
          <h2 id="nx-home-scout" className="nx-headline nx-home-scout-title">
            Call it before<br className="nx-home-wide-only" /> everyone else
          </h2>
          <p className="nx-home-scout-text">
            Co-sign the artists you believe in. When they grow, you earn Scout Score and climb the scout rankings.
          </p>
        </div>
        <ol className="nx-home-steps">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="nx-home-step">
              <span className="nx-home-step-num" aria-hidden="true">{i + 1}</span>
              <span>
                <span className="nx-home-step-title">{t}</span>
                <span className="nx-home-step-text">{d}</span>
              </span>
            </li>
          ))}
        </ol>
        <div className="nx-home-scout-cta">
          {signedIn
            ? <Link to="/leaderboard" className="nx-home-btn nx-home-btn-primary">Find your next co-sign</Link>
            : <Link to="/signup?as=fan" className="nx-home-btn nx-home-btn-primary">Start scouting, free</Link>}
        </div>
      </div>
    </section>
  );
}

// Square artist tile for the trending row: the photo, or big initials.
function ArtistTile({ name, photoURL }) {
  const [failed, setFailed] = useState(false);
  const initials = (name || "?").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  return (
    <span className="nx-home-tile-art" aria-hidden="true">
      <span className="nx-home-tile-initials">{initials}</span>
      {photoURL && !failed && (
        <img src={photoURL} alt="" referrerPolicy="no-referrer" decoding="async" loading="lazy" draggable={false} onError={() => setFailed(true)} />
      )}
    </span>
  );
}

export default Home;
